import { ReactNode } from 'react';

interface NumProps {
  /** Numeric value. Pass null/undefined to render the muted placeholder. */
  value?: number | null;
  /** Format style: plain integer, currency ($), or signed (+/−). */
  format?: 'int' | 'currency' | 'signed' | 'percent' | 'ratio';
  /** Number of fraction digits. Defaults: int=0, currency=0, signed=0, percent=1, ratio=2. */
  digits?: number;
  /** Force a tone regardless of sign. */
  tone?: 'up' | 'down' | 'urgent' | 'muted' | 'default';
  /** Color positive/negative automatically (signed/percent only). */
  autoColor?: boolean;
  /** Placeholder shown for null/undefined values. */
  placeholder?: ReactNode;
}

const toneClasses = {
  up: 'text-success font-semibold',
  down: 'text-danger font-semibold',
  urgent: 'text-danger font-semibold',
  muted: 'text-text-3',
  default: 'text-text',
};

export function Num({
  value,
  format = 'int',
  digits,
  tone = 'default',
  autoColor = false,
  placeholder = '—',
}: NumProps) {
  if (value == null || !Number.isFinite(value)) {
    return <span className="font-mono text-[12.5px] text-text-3">{placeholder}</span>;
  }

  const fractionDigits =
    digits ?? (format === 'percent' ? 1 : format === 'ratio' ? 2 : 0);

  let display: string;
  switch (format) {
    case 'currency':
      display = '$' + value.toLocaleString('en-US', {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      });
      break;
    case 'percent':
      display = value.toLocaleString('en-US', {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }) + '%';
      break;
    case 'ratio':
      display = value.toLocaleString('en-US', {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }) + '×';
      break;
    case 'signed':
      display = (value >= 0 ? '+' : '') + value.toLocaleString('en-US', {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      });
      break;
    default:
      display = value.toLocaleString('en-US', {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      });
  }

  let appliedTone = tone;
  if (autoColor && tone === 'default') {
    if (value > 0) appliedTone = 'up';
    else if (value < 0) appliedTone = 'down';
  }

  return (
    <span className={`font-mono text-[12.5px] tabular-nums ${toneClasses[appliedTone]}`}>
      {display}
    </span>
  );
}
